/**
 * CSVBulkImportButton Component
 * Button for uploading CSV files to bulk import users.
 * Handles file selection, validation, and CSV parsing.
 */

import { useRef } from 'react';
import { Button } from '@fluentui/react-components';
import { ArrowUpload24Regular } from '@fluentui/react-icons';
import Papa from 'papaparse';

interface CSVBulkImportButtonProps {
  disabled?: boolean;
  onCSVParsed: (data: ParsedCSVData) => void;
  onError: (error: string) => void;
}

export interface ParsedCSVData {
  headers: string[];
  rows: Record<string, string>[];
  fileName: string;
  tapConfig?: {
    tap_lifetime_minutes?: number;
    tap_one_time_use?: boolean;
  };
}

const MAX_FILE_SIZE_MB = 5;
const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_MB * 1024 * 1024;

export function CSVBulkImportButton({
  disabled = false,
  onCSVParsed,
  onError,
}: CSVBulkImportButtonProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleButtonClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) {
      return;
    }

    // Reset input value for next selection
    event.target.value = '';

    // Validate file type
    if (!file.name.toLowerCase().endsWith('.csv')) {
      onError('Please select a CSV file (.csv)');
      return;
    }

    // Validate file size
    if (file.size > MAX_FILE_SIZE_BYTES) {
      onError(`File size exceeds ${MAX_FILE_SIZE_MB}MB limit`);
      return;
    }

    // Read file first to check for version header
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const content = e.target?.result as string;

        // Check for AAD version header and skip it
        const lines = content.split('\n');
        let csvContent = content;
        if (lines[0]?.toLowerCase().startsWith('version:')) {
          // Skip version header line
          csvContent = lines.slice(1).join('\n');
        }

        // Parse CSV file
        Papa.parse(csvContent, {
          header: true,
          skipEmptyLines: true,
          complete: (results) => {
            try {
              if (results.errors.length > 0) {
                const errorObj = results.errors[0];
                const errorMsg = typeof errorObj === 'string'
                  ? errorObj
                  : errorObj?.message || JSON.stringify(errorObj);
                onError(`CSV parsing error: ${errorMsg}`);
                return;
              }

              if (results.data.length === 0) {
                onError('CSV file is empty');
                return;
              }

              // Extract headers from first row
              const rawHeaders = results.meta.fields || [];
              if (rawHeaders.length === 0) {
                onError('CSV file has no columns');
                return;
              }

              // Parse AAD bracket notation: "Name [displayName] Required" -> "displayName"
              const extractFieldName = (header: string): string => {
                const match = header.match(/\[([^\]]+)\]/);
                return match ? match[1] : header.trim();
              };

              const headers = rawHeaders.map(extractFieldName);

              // Remap row data to use extracted field names
              const rows = (results.data as Record<string, string>[]).map((row) => {
                const newRow: Record<string, string> = {};
                rawHeaders.forEach((rawHeader, index) => {
                  const fieldName = headers[index];
                  newRow[fieldName] = row[rawHeader] || '';
                });
                return newRow;
              });

              onCSVParsed({
                headers,
                rows,
                fileName: file.name,
              });
            } catch (err) {
              const errorMsg = err instanceof Error ? err.message : String(err);
              onError(`Error processing CSV data: ${errorMsg}`);
            }
          },
          error: (error: Error) => {
            const errorMsg = error?.message || String(error) || 'Unknown parsing error';
            onError(`Failed to parse CSV: ${errorMsg}`);
          },
        });
      } catch (err) {
        const errorMsg = err instanceof Error ? err.message : String(err);
        onError(`Error reading CSV file: ${errorMsg}`);
      }
    };

    reader.onerror = () => {
      onError('Failed to read file');
    };

    reader.readAsText(file);
  };

  return (
    <>
      <Button
        appearance="primary"
        icon={<ArrowUpload24Regular />}
        onClick={handleButtonClick}
        disabled={disabled}
        data-testid="csv-bulk-import-button"
      >
        Import CSV
      </Button>
      <input
        ref={fileInputRef}
        type="file"
        accept=".csv"
        style={{ display: 'none' }}
        onChange={handleFileChange}
        data-testid="csv-file-input"
      />
    </>
  );
}
